import clsx from "clsx";

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  ariaLabel?: string;
  /** Material Symbols glyph name */
  icon?: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  fullWidth?: boolean;
  /** `lg` gives each option a 40px touch target; `sm` narrows the padding
   * for crowded rows. */
  size?: "sm" | "md" | "lg";
  "aria-label"?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  fullWidth = false,
  size = "md",
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={clsx(
        "items-center gap-0.5 rounded-lg bg-subtle p-0.5",
        fullWidth ? "grid w-full grid-flow-col auto-cols-fr" : "inline-flex",
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-label={option.ariaLabel}
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={clsx(
              "flex min-w-0 items-center gap-1.5 whitespace-nowrap rounded-md font-semibold cursor-pointer transition-colors",
              size === "lg" ? "min-h-10 py-2 text-sm" : "py-1 text-xs",
              fullWidth
                ? "justify-center px-1.5"
                : size === "sm"
                  ? "px-2"
                  : "px-3",
              active
                ? "bg-white text-primary shadow-sm"
                : "text-secondary hover:text-primary",
            )}
          >
            {option.icon && (
              <span
                className="material-symbols-outlined text-sm leading-none"
                aria-hidden
              >
                {option.icon}
              </span>
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
